/**
 * PEStart Businessplan PDF — jsPDF Fließtext-Export (bankenfähig, weiß).
 */

import jsPDF from 'jspdf';
import type { BusinessPlanContext, GeneratedSectionText } from './PEStartTextEngine';

export type PEStartBusinessPlanPdfInput = {
    sections: GeneratedSectionText[];
    context: BusinessPlanContext;
    /** Fallback, wenn stammdaten.firmenname leer */
    clientLabel?: string;
    /** Fallback Adresszeile */
    clientSubline?: string;
};

const MARGIN = 18;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FOOTER_Y = PAGE_H - 12;
const BOTTOM_LIMIT = PAGE_H - 22;

function formatEur(value: number, _currencyCode?: string): string {
    void _currencyCode;
    // Helvetica in jsPDF rendert € und schmale Leerzeichen oft als "····" — daher ASCII.
    const num = Math.round(Number(value) || 0).toLocaleString('de-DE', {
        maximumFractionDigits: 0,
        useGrouping: true,
    });
    return `${num} EUR`;
}

function sanitizePdfText(text: string): string {
    return String(text || '')
        .replace(/[\u00A0\u202F\u2007\u2009\u2008\u200A]/g, ' ')
        .replace(/€/g, 'EUR');
}

function formatDateDe(isoOrDate?: string): string {
    const d = isoOrDate ? new Date(isoOrDate) : new Date();
    if (Number.isNaN(d.getTime())) {
        return new Date().toLocaleDateString('de-DE');
    }
    return d.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
    if (y + needed <= BOTTOM_LIMIT) return y;
    doc.addPage();
    return MARGIN + 4;
}

function drawFooter(doc: jsPDF, pageIndex: number, pageCount: number): void {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text('PreisExpert · PEStart · Vertraulich', MARGIN, FOOTER_Y);
    doc.text(`Seite ${pageIndex} / ${pageCount}`, PAGE_W - MARGIN, FOOTER_Y, {
        align: 'right',
    });
}

/** Label links, Betrag rechts fluchtend — ohne Überlappung / Punkte-Artefakte */
function drawKvRows(doc: jsPDF, y: number, rows: [string, string][]): number {
    const valueColW = 42;
    const labelMaxW = CONTENT_W - valueColW - 4;
    const valueX = PAGE_W - MARGIN;

    doc.setFontSize(9);
    for (const [labelRaw, valueRaw] of rows) {
        const label = sanitizePdfText(labelRaw);
        const value = sanitizePdfText(valueRaw);
        const labelLines = doc.splitTextToSize(label, labelMaxW);
        const blockH = Math.max(labelLines.length * 4.2, 5.5) + 1.5;
        y = ensureSpace(doc, y, blockH);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(70, 70, 70);
        doc.text(labelLines, MARGIN, y);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(20, 20, 20);
        doc.text(value, valueX, y, { align: 'right' });

        y += blockH;
    }
    return y;
}

function drawPrivatkostenBlock(
    doc: jsPDF,
    y: number,
    context: BusinessPlanContext
): number {
    const currency = context.meta.currencyCode || 'EUR';
    const pk = context.privatkostenAufschluesselung;
    if (!pk) return y;

    y = ensureSpace(doc, y, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text('Privatkostenkompass — Unternehmerlohn', MARGIN, y);
    y += 6;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    const hint = doc.splitTextToSize(pk.hinweis || '', CONTENT_W);
    doc.text(hint, MARGIN, y);
    y += hint.length * 4 + 3;

    const rows: [string, string][] = pk.kategorien.map((k) => [
        k.label,
        formatEur(k.betragMonat, currency),
    ]);
    rows.push(['Summe Unternehmerlohn (Monat)', formatEur(pk.summeMonat, currency)]);
    rows.push(['Summe gerundet (Bank)', formatEur(pk.summeGerundet, currency)]);
    y = drawKvRows(doc, y, rows);
    y += 4;
    doc.setDrawColor(200, 200, 200);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    return y + 8;
}

function drawGruenderProfilBlock(
    doc: jsPDF,
    y: number,
    context: BusinessPlanContext
): number {
    const currency = context.meta.currencyCode || 'EUR';
    const notes = String(context.executiveSummaryNotes || '').trim();
    const blocks: Array<{ title: string; body: string }> = [
        { title: 'Executive Summary / Vorhabensnotizen', body: notes },
        {
            title: 'Fachliche Qualifikation & Titel',
            body: String(context.fachlicheQualifikation || '').trim(),
        },
        {
            title: 'Berufserfahrung & Praxis',
            body: String(context.berufserfahrung || '').trim(),
        },
        {
            title: 'Kaufmännische & unternehmerische Eignung',
            body: String(context.kaufmaennischeEignung || '').trim(),
        },
    ].filter((b) => b.body.length > 0);

    const hasVorsorge =
        Number(context.privateAltersvorsorge) > 0 ||
        Number(context.liquiditaetspufferMonat) > 0;

    if (blocks.length === 0 && !hasVorsorge) return y;

    y = ensureSpace(doc, y, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text('Gruenderprofil & Vorsorge (PEStart / KfW)', MARGIN, y);
    y += 7;

    for (const block of blocks) {
        y = ensureSpace(doc, y, 14);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(40, 40, 40);
        doc.text(sanitizePdfText(block.title), MARGIN, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(50, 50, 50);
        const lines = doc.splitTextToSize(sanitizePdfText(block.body), CONTENT_W);
        for (const line of lines) {
            y = ensureSpace(doc, y, 5);
            doc.text(line, MARGIN, y);
            y += 4.5;
        }
        y += 3;
    }

    y = drawKvRows(doc, y, [
        [
            'Private Altersvorsorge (EUR / Monat)',
            formatEur(context.privateAltersvorsorge || 0, currency),
        ],
        [
            'Privater Liquiditaetspuffer (EUR / Monat)',
            formatEur(context.liquiditaetspufferMonat || 0, currency),
        ],
    ]);

    doc.setDrawColor(200, 200, 200);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    return y + 8;
}

function drawBwaBlock(doc: jsPDF, y: number, context: BusinessPlanContext): number {
    const currency = context.meta.currencyCode || 'EUR';
    const bwa = context.bwaErtragslage;
    if (!bwa) return y;

    y = ensureSpace(doc, y, 24);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text('BWA-Ertragslage (Monatsbasis)', MARGIN, y);
    y += 7;

    y = drawKvRows(doc, y, [
        ['Planumsatz netto', formatEur(bwa.basis.planumsatzNetto, currency)],
        [
            `Wareneinsatz (${Number(bwa.basis.baseMaterialProzent || 0).toFixed(1)} %)`,
            formatEur(bwa.basis.materialkostenMonat, currency),
        ],
        [
            'Personalkosten brutto',
            formatEur(bwa.basis.personalkostenBrutto, currency),
        ],
        ['Raumkosten', formatEur(bwa.basis.raumkostenMonat, currency)],
        [
            'Sonstige Fixkosten (Betrieb)',
            formatEur(bwa.basis.sonstigeFixkostenMonat, currency),
        ],
        [
            'Kapitaldienst / Monat',
            formatEur(bwa.basis.kapitaldienstMonat, currency),
        ],
    ]);
    y += 4;

    const lineKeys: Array<[string, keyof (typeof bwa.monate)[0]]> = [
        ['Umsatzerlöse', 'umsatzerloese'],
        ['- Material/Ware', 'materialaufwand'],
        ['= Rohertrag', 'rohertrag'],
        ['- Personalkosten', 'personalkosten'],
        ['- Raum & Fix', 'raumUndFixkosten'],
        ['= EBITDA', 'operativesErgebnisEbitda'],
        ['- Kapitaldienst', 'kapitaldienst'],
        ['= Vorsteuer', 'vorsteuerErgebnis'],
        ['Liquidität', 'liquiditaetssaldo'],
    ];

    const colCount = Math.max(1, bwa.monate.length);
    const labelW = 32;
    const colW = (CONTENT_W - labelW) / colCount;

    y = ensureSpace(doc, y, 10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(90, 90, 90);
    doc.text('Pos.', MARGIN, y);
    bwa.monate.forEach((m, i) => {
        doc.text(`M${m.month}`, MARGIN + labelW + i * colW + colW, y, {
            align: 'right',
        });
    });
    y += 4.5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    for (const [label, key] of lineKeys) {
        y = ensureSpace(doc, y, 5);
        const rowY = y;
        doc.setTextColor(70, 70, 70);
        doc.text(label, MARGIN, rowY);
        doc.setTextColor(20, 20, 20);
        for (let i = 0; i < bwa.monate.length; i++) {
            const m = bwa.monate[i];
            const num = Math.round(Number(m[key]) || 0).toLocaleString('de-DE');
            doc.text(num, MARGIN + labelW + i * colW + colW, rowY, {
                align: 'right',
            });
        }
        y = rowY + 4.2;
    }

    y += 3;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text('Beträge in Euro (volle Euro, ohne Cent).', MARGIN, y);
    y += 6;
    doc.setDrawColor(200, 200, 200);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    return y + 8;
}

function drawCover(doc: jsPDF, input: PEStartBusinessPlanPdfInput): number {
    const { context, clientLabel, clientSubline } = input;
    const currency = context.meta.currencyCode || 'EUR';
    const st = context.stammdaten;
    let y = MARGIN;

    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, PAGE_W, PAGE_H, 'F');

    // --- Briefkopf (bankenkonform) ---
    doc.setDrawColor(30, 30, 30);
    doc.setLineWidth(0.6);
    doc.line(MARGIN, y + 2, PAGE_W - MARGIN, y + 2);

    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text('PreisExpert', MARGIN, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    doc.text('PEStart · Businessplan & Anlaufprognose', PAGE_W - MARGIN, y, {
        align: 'right',
    });

    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(20, 20, 20);
    doc.text(st?.inhaberName || '—', MARGIN, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    const firma =
        (st?.firmenname && st.firmenname !== '—'
            ? st.firmenname
            : clientLabel || ''
        ).trim() || '—';
    doc.text(firma, MARGIN, y);
    y += 5.5;
    const adresse =
        (st?.adresseZeile && st.adresseZeile !== '—'
            ? st.adresseZeile
            : clientSubline || ''
        ).trim() || '—';
    const addrLines = doc.splitTextToSize(adresse, CONTENT_W);
    doc.setTextColor(70, 70, 70);
    doc.text(addrLines, MARGIN, y);
    y += addrLines.length * 5 + 4;

    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text(
        `Datum: ${st?.datum || formatDateDe(context.meta.generatedAt)}`,
        MARGIN,
        y
    );
    y += 5;
    doc.text(
        `Gründungstyp: ${st?.foundingTypeLabel || context.meta.foundingTypeLabel}`,
        MARGIN,
        y
    );
    y += 8;

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(20, 20, 20);
    doc.text('Businessplan & Anlaufprognose', MARGIN, y);
    y += 12;

    // KPI-Block inkl. Finanz-Split
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text('Kennzahlen-Zusammenfassung (volle Euro)', MARGIN, y);
    y += 8;

    const ms = context.managementSummary;
    const fin = context.finanzierungUndKapitalbedarf;
    const kpiRows: [string, string][] = [
        ['Umsatzziel (Totpunkt brutto / Monat)', formatEur(ms.totpunktMonatBrutto, currency)],
        [
            ms.unternehmerlohnSource === 'kompass'
                ? 'Unternehmerlohn (Privatkostenkompass)'
                : 'Unternehmerlohn (Pauschale)',
            formatEur(ms.unternehmerlohnKompass, currency),
        ],
        ['Planumsatz netto / Monat', formatEur(ms.planumsatzNetto, currency)],
        [
            'Investitionen (gerundet)',
            formatEur(fin.investitionenGerundet, currency),
        ],
        [
            'Betriebsmittel- & Anlaufpuffer',
            formatEur(fin.anlaufPufferGerundet, currency),
        ],
        [
            'Gesamtkapitalbedarf',
            formatEur(fin.gesamtkapitalbedarfGerundet, currency),
        ],
        ['Kreditbetrag', formatEur(fin.kreditbetragGerundet, currency)],
        [
            'Break-Even',
            ms.breakEvenMonat ? `Monat ${ms.breakEvenMonat}` : 'offen (innerhalb M1–6)',
        ],
    ];

    y = drawKvRows(doc, y, kpiRows);
    y += 4;
    doc.setDrawColor(200, 200, 200);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 8;

    y = drawGruenderProfilBlock(doc, y, context);
    y = drawPrivatkostenBlock(doc, y, context);
    y = drawBwaBlock(doc, y, context);

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    const note = doc.splitTextToSize(
        'Die folgenden Kapitel basieren auf den Live-Kennzahlen aus PreisExpert. Geldbeträge sind kaufmännisch auf volle Euro gerundet. Personal nur als Rollen/Anzahlen (keine Vornamen).',
        CONTENT_W
    );
    y = ensureSpace(doc, y, note.length * 4.5 + 4);
    doc.text(note, MARGIN, y);
    y += note.length * 4.5 + 10;

    return y;
}

function drawChapter(
    doc: jsPDF,
    y: number,
    chapterIndex: number,
    title: string,
    body: string
): number {
    y = ensureSpace(doc, y, 20);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    const heading = `${chapterIndex}. ${title.replace(/^\d+\.\s*/, '')}`;
    const headLines = doc.splitTextToSize(heading, CONTENT_W);
    doc.text(headLines, MARGIN, y);
    y += headLines.length * 6.5 + 2;

    doc.setDrawColor(230, 194, 153);
    doc.setLineWidth(0.8);
    doc.line(MARGIN, y, MARGIN + 28, y);
    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(35, 35, 35);

    const paragraphs = sanitizePdfText(String(body || ''))
        .replace(/\r\n/g, '\n')
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean);

    if (paragraphs.length === 0) {
        doc.setTextColor(120, 120, 120);
        doc.text('— Kein Text für dieses Kapitel —', MARGIN, y);
        return y + 10;
    }

    const lineHeight = 5.2;
    for (const para of paragraphs) {
        const lines = doc.splitTextToSize(para.replace(/\n/g, ' '), CONTENT_W);
        for (const line of lines) {
            y = ensureSpace(doc, y, lineHeight + 1);
            doc.text(line, MARGIN, y);
            y += lineHeight;
        }
        y += 3.5;
    }

    return y + 6;
}

/**
 * Erzeugt und speichert den PEStart-Businessplan als PDF.
 */
export function downloadPEStartBusinessPlanPdf(
    input: PEStartBusinessPlanPdfInput
): void {
    if (!input.sections?.length) {
        throw new Error('Keine generierten Kapitel für den PDF-Export.');
    }

    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    let y = drawCover(doc, input);

    input.sections.forEach((sec, idx) => {
        y = drawChapter(
            doc,
            y,
            idx + 1,
            sec.title || `Kapitel ${idx + 1}`,
            sec.text || ''
        );
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        drawFooter(doc, i, pageCount);
    }

    const salonSafe = (
        input.context.stammdaten?.firmenname ||
        input.clientLabel ||
        'Salon'
    )
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '')
        .replace(/\s+/g, '_')
        .slice(0, 40) || 'Salon';
    const dateIso = new Date().toISOString().slice(0, 10);
    doc.save(`PEStart_Businessplan_${salonSafe}_${dateIso}.pdf`);
}
